'use client';

import * as React from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type Table as TableType,
  Row
} from '@tanstack/react-table';

import { DataTablePagination } from './data-table-pagination';
import {
  DataTableDeleteAction,
  DataTableHeaderAction,
  DataTableToolbar
} from './data-table-toolbar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from './table';
import { FilterOption } from './data-table-faceted-filter';
import { OptionTreeItemType } from 'types/common';

export interface DataTableProps<TData, TValue> {
  fetch?: Promise<{
    data: any[];
    totalCount: number;
  }>;
  columns: ColumnDef<TData, TValue>[];
  data?: TData[];
  toolbar?: boolean;
  header?: boolean;
  pager?: boolean;
  remote?: boolean;
  totalCount?: number;
  filterableColumns?: {
    id: string;
    title: string;
    options: FilterOption[] | OptionTreeItemType[];
    tree?: boolean;
  }[];
  searchableColumns?: {
    id: string;
    title: string;
    className?: string;
  }[];
  rangableColumns?: {
    id: string;
    title: string;
    className?: string;
  }[];
  onDelete?: (rows: TData[], table: TableType<TData>) => Promise<void>;
  onDeleteValidate?: (rows: TData[], table: TableType<TData>) => boolean;
  initialColumnVisibility?: Record<string, boolean>;
  toolbarActions?: DataTableHeaderAction<TData>[];
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean) | undefined;
  getRowClassName?: (row: Row<TData>) => string;
  deleteAction?: DataTableDeleteAction;
  meta?: Record<string, unknown>;
  hasBorder?: boolean;
  isNotRemovePram?: URLSearchParams;
  isViewOption?: boolean;
  manualPagination?: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  toolbar,
  header,
  pager,
  onDelete,
  onDeleteValidate,
  toolbarActions,
  getRowClassName,
  deleteAction,
  hasBorder,
  meta = {},
  filterableColumns = [],
  searchableColumns = [],
  rangableColumns = [],
  initialColumnVisibility = {},
  enableRowSelection = true,
  isNotRemovePram,
  isViewOption = true
}: //   filters
DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>(initialColumnVisibility);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    data: data || [],
    columns,
    meta,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters
    },
    enableRowSelection,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues()
  });

  return (
    <div className="space-y-4 w-full overflow-auto p-1">
      {toolbar && (
        <DataTableToolbar
          table={table}
          filterableColumns={filterableColumns}
          searchableColumns={searchableColumns}
          rangableColumns={rangableColumns}
          onDelete={onDelete}
          onDeleteValidate={onDeleteValidate}
          actions={toolbarActions}
          deleteAction={deleteAction}
          isNotRemovePram={isNotRemovePram}
          isViewOption={isViewOption}
        />
      )}
      <div className="rounded-md border">
        <Table>
          {header && (
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        className={
                          hasBorder ? '[&:not(:last-child)]:border-r' : ''
                        }
                        key={header.id}
                        colSpan={header.colSpan}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
          )}
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={getRowClassName ? getRowClassName(row) : ''}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  조회 결과가 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {pager && (
        <DataTablePagination table={table} totalCount={data?.length ?? 0} />
      )}
    </div>
  );
}
